import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Paper, Typography, Container, CircularProgress, Grid, Button, Stack } from '@mui/material';
import { publicApi } from '../../../services/publicApi';
import RedemptionStatus from './RedemptionStatus';
import type { CouponData, PreviewDesignElement } from '../../../types/coupon';
import Footer from '../../../components/shared/Footer';

/**
 * Local CouponData interface for the redemption page.
 * Like CouponLanding, this includes the 'branding' field which is transformed 
 * from 'customBranding' by the API's mergeBrandingSettings function.
 */
interface RedemptionCouponData extends CouponData {
    customBranding?: {
        enabled: boolean;
        company?: {
            logo: {
                url: string;
                width: number;
            };
            name: string;
            websiteUrl: string;
            socialLinks: {
                [key: string]: string;
            };
        };
        theme?: {
            primaryColor: string;
            secondaryColor: string;
            backgroundColor: string;
            textColor: string;
            fontFamily: string;
        };
        landingPage?: {
            welcomeHeading: string;
            welcomeMessage: string;
            buttonText: string;
            footerText: string;
        };
    };
    branding?: {
        theme: {
            primaryColor: string;
            secondaryColor: string;
            backgroundColor: string;
            textColor: string;
            fontFamily: string;
        };
        company: {
            logo: {
                url: string;
                width: number;
            };
            name: string;
            websiteUrl: string;
            socialLinks: {
                [key: string]: string;
            };
        };
        landingPage: {
            welcomeHeading: string;
            welcomeMessage: string;
            defaultWelcomeHeading: string;
            defaultWelcomeMessage: string;
            defaultButtonText: string;
            defaultFooterText: string;
        };
    };
}

interface Redemption {
    firstName?: string;
    lastName?: string;
    customerEmail?: string;
    redeemedAt?: string;
}

interface Props {
    onCouponLoad?: (coupon: RedemptionCouponData) => void;
}

const CouponRedemption: React.FC<Props> = ({ onCouponLoad }) => {
    const { couponId, email, subscriberId } = useParams<{ couponId: string; email: string; subscriberId: string }>();
    
    const [coupon, setCoupon] = useState<RedemptionCouponData | null>(null);
    const [redemption, setRedemption] = useState<Redemption | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [personalQRCode, setPersonalQRCode] = useState<string>();

    useEffect(() => {
        let mounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!couponId || !email || !subscriberId) {
                    throw new Error('Missing required URL parameters');
                }

                // First get coupon data
                const couponResponse = await publicApi.getCouponDetails(couponId);
                if (!mounted) return;
                
                const couponData = couponResponse.data.data.coupon as RedemptionCouponData;
                setCoupon(couponData);

                // Notify parent of loaded coupon data for theming
                if (mounted && onCouponLoad) {
                    onCouponLoad(couponData);
                }

                // Get subscriber details to get customer name
                const subscriberResponse = await publicApi.getSubscriberDetails(couponId, subscriberId);
                if (!mounted) return;

                // Get customer name from subscriber data
                const { firstName, lastName } = subscriberResponse.data.data.subscriber;
                const customerName = `${firstName || ''} ${lastName || ''}`.trim() || email;

                // Check redemption status
                const redemptionResponse = await publicApi.getRedemptionStatus(couponId, { 
                    email, 
                    subscriberId 
                });

                if (!mounted) return;

                // Set redemption if it exists
                if (redemptionResponse.data?.data?.redemption) {
                    setRedemption(redemptionResponse.data.data.redemption);
                }

                // Generate QR code image with proper customer name from subscriber data
                const imageResponse = await publicApi.generateCouponImage(couponId, {
                    customerName,
                    customerEmail: email,
                    subscriberId
                });
                
                if (!mounted) return;
                
                const blob = new Blob([imageResponse.data], { type: 'image/png' });
                const qrCodeUrl = URL.createObjectURL(blob);
                setPersonalQRCode(qrCodeUrl);

            } catch (err: any) {
                if (mounted) {
                    setError(err.response?.data?.message || err.message || 'Failed to load coupon');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            mounted = false;
        };
    }, [couponId, email, subscriberId]);

    const handleRedeem = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await publicApi.redeemCoupon(couponId!, {
                email: email!,
                subscriberId: subscriberId!
            });

            // Safely set redemption data
            if (response.data?.data?.redemption) {
                setRedemption(response.data.data.redemption);
            } else {
                throw new Error('Failed to redeem coupon');
            }

        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to redeem coupon');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box 
                display="flex" 
                flexDirection="column"
                minHeight="100vh"
                bgcolor={coupon?.branding?.theme?.backgroundColor || 'transparent'}
            >
                <Box 
                    flexGrow={1}
                    display="flex" 
                    justifyContent="center" 
                    alignItems="center"
                >
                    <CircularProgress />
                </Box>
                <Footer 
                    variant="public" 
                    backgroundColor={coupon?.branding?.theme?.backgroundColor}
                    branding={coupon?.branding}
                />
            </Box>
        );
    }

    if (error || !coupon) {
        return (
            <Container maxWidth="sm">
                <Paper 
                    sx={{ 
                        p: 3, 
                        mt: 3, 
                        textAlign: 'center',
                    }}
                >
                    <Typography color="error" variant="h6">
                        {error || 'Failed to load coupon data'}
                    </Typography>
                </Paper>
            </Container>
        );
    }

    const isValid = coupon.isActive && 
                   new Date() >= new Date(coupon.validFrom) && 
                   new Date() <= new Date(coupon.validUntil);

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: coupon?.branding?.theme?.backgroundColor || 'transparent',
            py: { xs: 3, md: 4 }
        }}>
            <Container maxWidth="lg" sx={{ mb: { xs: 3, md: 4 } }}>
                <Paper 
                    elevation={2}
                    sx={{ 
                        p: { xs: 3, md: 4 }, 
                        borderRadius: 2, 
                        backgroundColor: 'white'
                    }}
                >
                    {/* Company Logo */}
                    {coupon.branding?.company?.logo?.url && (
                        <Box display="flex" justifyContent="center" mb={4}>
                            <img 
                                src={coupon.branding.company.logo.url}
                                alt={coupon.branding.company.name}
                                style={{ 
                                    maxWidth: '200px',
                                    height: 'auto'
                                }}
                            />
                        </Box>
                    )}

                    {/* Title Section */}
                    <Box mb={4} textAlign="center">
                        <Typography variant="h4" component="h1" gutterBottom>
                            {coupon.title}
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                            {coupon.description}
                        </Typography>
                    </Box>

                    {/* Grid Container */}
                    <Grid container spacing={6}>
                        {/* Left column: Coupon Preview */}
                        <Grid item xs={12} md={8} sx={{ mb: { xs: 4, md: 0 } }}>
                            {personalQRCode ? (
                                <Paper 
                                    sx={{ 
                                        p: 0, 
                                        overflow: 'hidden',
                                        position: 'relative',
                                        width: {
                                            xs: '100%',
                                            md: coupon.design.canvas.breakpoints.desktop.width
                                        },
                                        height: {
                                            xs: 'auto',
                                            md: coupon.design.canvas.breakpoints.desktop.height
                                        },
                                        margin: '0 auto',
                                        borderRadius: 2,
                                        boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <Box 
                                        component="img"
                                        src={personalQRCode}
                                        alt="Your personalized coupon"
                                        sx={{ 
                                            width: '100%',
                                            height: '100%',
                                            display: 'block',
                                            objectFit: {
                                                xs: 'contain',
                                                md: 'none'
                                            }
                                        }}
                                    />
                                </Paper>
                            ) : (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    height: 400
                                }}>
                                    <CircularProgress />
                                </Box>
                            )}
                        </Grid>

                        {/* Right column: Redemption Status */}
                        <Grid item xs={12} md={4}>
                            <Stack spacing={4}>
                                <RedemptionStatus
                                    isValid={isValid}
                                    isRedeemed={!!redemption}
                                    redeemedAt={redemption?.redeemedAt}
                                    onRedeem={handleRedeem}
                                    errorMessage={
                                        !isValid 
                                            ? 'This coupon has expired or is not yet valid'
                                            : undefined
                                    }
                                />
                                
                                {/* Coupon Details */}
                                <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Coupon Details
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Title:</strong> {coupon.title}
                                    </Typography>
                                    {coupon.description && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Description:</strong> {coupon.description}
                                        </Typography>
                                    )}
                                    {/*<Typography variant="body1" gutterBottom>
                                        <strong>Valid From:</strong> {new Date(coupon.validFrom).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Valid Until:</strong> {new Date(coupon.validUntil).toLocaleDateString()}
                                    </Typography>
                                    */}
                                    {coupon.minPurchaseAmount > 0 && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Minimum Purchase:</strong> ${coupon.minPurchaseAmount}
                                        </Typography>
                                    )}
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
            <Footer 
                variant="public" 
                backgroundColor={coupon?.branding?.theme?.backgroundColor}
                branding={coupon?.branding}
            />
        </Box>
    );
};

export default CouponRedemption;
